import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { clamp, forOwn, sortBy, values } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { ShapeLocationState } from '../../../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState } from '../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { ObjectWith } from '../../../../../../cross-team-shared/types/misc';
import { AbstractAnnotationStore } from '../../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { TagService } from '../../../../../../outputs/annotation/annotation-shared/utils/tag.service';
import { InFlightExtrapolationRequest } from '../../../../../../team-apis/content/in-flight-extrapolation-request';
import {
  FrameSpecificTag,
  PointType,
  VisibilityState,
} from '../../../../../../team-apis/otterdam/answers/shape-typings';
import { outputInfoStoreToken } from '../../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../h3ml/state/task-workspace-store';

interface Section {
  left: number;
  width: number;
  visibility: VisibilityState;
  mlExtrapolationWidth: number;
}

interface DynamicKeyframe {
  frameNumberPercent: number;
  blankRequired: boolean;
}

interface PendingMLSection {
  left: number;
  width: number;
}

@Component({
  selector: 'h3-focusable-timeline',
  templateUrl: './focusable-timeline.component.html',
  styleUrls: [
    './focusable-timeline.component.scss',
    '../../../../../../outputs/annotation/annotation-shared/visibility.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FocusableTimelineComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input() shapeStore?: Store<ShapeState>;
  @Input() showKeyframeIndicator = false;
  sections$!: Observable<Section[]>;
  dynamicKeyframes$!: Observable<DynamicKeyframe[]>;
  pendingExtrapolationSections$!: Observable<PendingMLSection[]>;
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private outputInfoStore = inject(outputInfoStoreToken);

  constructor(
    private store: AbstractAnnotationStore,
    private tagService: TagService,
    injector: Injector,
  ) {
    super(injector);
  }

  trackByIndex = (index: number) => index;

  ngOnInit() {
    this.sections$ = this.getShapeStoreOrEmpty$((shapeStore) =>
      combineLatest([
        shapeStore('key_locations').$.pipe(filter(isDefined)),
        this.getMaxFrameNumber$().pipe(take(1)),
      ]).pipe(
        map(([keyLocations, maxFrameNumber]) =>
          generateSections(keyLocations, maxFrameNumber),
        ),
      ),
    );

    this.dynamicKeyframes$ = this.getShapeStoreOrEmpty$((shapeStore) =>
      combineLatest([
        shapeStore('dynamic_tags').$.pipe(filter(isDefined)),
        this.getMaxFrameNumber$().pipe(take(1)),
      ]).pipe(
        map(([dynamicTags, maxFrameNumber]) =>
          // @ts-ignore -- fix me
          this.generateDynamicKeyframes(dynamicTags, maxFrameNumber),
        ),
      ),
    );

    this.subscribeToInFlightExtrapolationSections();
  }

  private subscribeToInFlightExtrapolationSections() {
    this.pendingExtrapolationSections$ = this.getShapeStoreOrEmpty$(
      (shapeStore) =>
        combineLatest([
          shapeStore('index').$.pipe(filter(isDefined)),
          this.store('inFlightMlExtrapolationRequests').$.pipe(
            filter(isDefined),
          ),
          this.getMaxFrameNumber$().pipe(take(1)),
        ]).pipe(
          map(([shapeIndex, request, maxFrameNumber]) =>
            generatePendingExtrapolationSection(
              shapeIndex,
              request,
              maxFrameNumber,
            ),
          ),
        ),
    );
  }

  private getMaxFrameNumber$() {
    return this.taskWorkspaceStore.getMaxFrameNumber$(
      this.outputInfoStore.state().output.name,
    );
  }

  private generateDynamicKeyframes(
    dynamicTags: ObjectWith<FrameSpecificTag[]>,
    maxFrameNumber: number,
  ) {
    const byFrameNumber: ObjectWith<DynamicKeyframe> = {};
    forOwn(dynamicTags, (tags, outputName) => {
      tags.forEach(({ frame_number: frameNumber, value: tag }) => {
        let keyframe = byFrameNumber[frameNumber];
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        if (!keyframe) {
          keyframe = byFrameNumber[frameNumber] = {
            frameNumberPercent: (frameNumber / maxFrameNumber) * 100,
            blankRequired: false,
          };
        }
        if (this.tagService.isTagRequiredAndBlank(tag, outputName)) {
          keyframe.blankRequired = true;
        }
      });
    });

    return sortBy(
      values(byFrameNumber),
      (keyframe) =>
        // sort blank+required ones to the end, so they show up when indicators
        // overlap
        keyframe.frameNumberPercent + (keyframe.blankRequired ? 100 : 0),
    );
  }

  private getShapeStoreOrEmpty$<T>(
    switchMapCallback: (shapeStore: Store<ShapeState>) => Observable<T>,
  ) {
    return this.getInput$('shapeStore').pipe(
      switchMap((shapeStore) =>
        !shapeStore ? of([]) : switchMapCallback(shapeStore),
      ),
    );
  }
}

function generatePendingExtrapolationSection(
  shapeIndex: number,
  inFlightRequests: InFlightExtrapolationRequest[],
  maxFrameNumber: number,
) {
  const pendingExtrapolationSections: PendingMLSection[] = [];

  inFlightRequests.forEach((request) => {
    if (request.shapeId === shapeIndex) {
      const preclampedWidth =
        ((request.requestedExtrapolatedFramesCount + 1) / maxFrameNumber) * 100;

      const left = (request.keyFrameNumber / maxFrameNumber) * 100;

      pendingExtrapolationSections.push({
        left,
        width: clamp(preclampedWidth, 0, 100 - left),
      });
    }
  });
  return pendingExtrapolationSections;
}

function generateSections(
  keyLocations: Array<ShapeLocationState<PointType>>,
  maxFrameNumber: number,
) {
  let lastLeft = 100;
  return keyLocations
    .slice()
    .reverse()
    .map((keyLocation) => {
      const left = (keyLocation.frame_number / maxFrameNumber) * 100;
      const width = lastLeft - left;
      lastLeft = left;
      const visibility = keyLocation.visibility;
      let mlExtrapolationWidth = 0;
      if (keyLocation.extrapolation) {
        // The purple frames should start at the originating keyframe and then
        // extend until the number of frames extrapolated so we need the +1.
        const unadjustedMlExtrapolationWidth =
          ((keyLocation.extrapolation.points.length + 1) * 100 * 100) /
          (width * maxFrameNumber);
        mlExtrapolationWidth = clamp(unadjustedMlExtrapolationWidth, 0, 100);
      }

      return { left, width, visibility, mlExtrapolationWidth };
    });
}
